import { DatePipe } from '@angular/common';
import { inject, Injectable } from '@angular/core';

import { SharedLanguageService } from '@language';
import { DateRange } from '../../core/dates/entities/date-range.entity';
import { IsDateRangeQuery, ParseDateRangeQuery } from '../../core/dates/helpers/date-range-query-helper';
import { DEFAULT_FORMAT, MONTH_YEAR, YEAR } from '../../core/dates/helpers/format-date.helper';
import { FieldType } from '../../core/field-definitions/entities/field-definition.entity';
import { DottedKeyToFlatKey } from '../../core/field-definitions/helpers/dottedkey-to-flatkey.helper';
import { ExtendedFacetProfileField } from '../../core/profiles/entities/extended-profile-field.entity';
import { ProfileField } from '../../core/profiles/entities/profile-field.entity';
import { RecordTypeTranslationService } from '../../core/record-types/services/record-type-translation.service';
import { RecordType } from '../../core/records/entities/metadata/metadata.entity';

@Injectable({ providedIn: 'root' })
export class ShFacetValueTranslationService {
    private readonly RECORD_TYPE_KEY: string = 'Administrative.RecordType';
    private readonly recordTypeTranslationService: RecordTypeTranslationService = inject(RecordTypeTranslationService);

    constructor(
        private readonly translateService: SharedLanguageService,
        private readonly datePipe: DatePipe
    ) {}

    public translateValue(
        facetProfileField: ExtendedFacetProfileField,
        value: string,
        isMissingValue: boolean
    ): string {
        if (isMissingValue) {
            return this.translateService.instant('facets.no-field-value', { field: facetProfileField.DisplayLabel });
        }

        if (facetProfileField.DottedKey === this.RECORD_TYPE_KEY) {
            return this.getRecordTypeTranslation(value);
        }

        if (facetProfileField.FieldDefinition?.Type === FieldType.BooleanField) {
            return this.translateService.instant(`overrides.BooleanField.${value === '1' ? 'true' : 'false'}`);
        }

        if (!IsDateRangeQuery(value)) {
            return this.getTranslatedFieldDefinitionValue(facetProfileField, value);
        }

        const dateRange = ParseDateRangeQuery(value);

        if (Number.isNaN(dateRange.LengthInDays) || dateRange.From === null) {
            return value;
        }

        const format = this.getDateFormat(dateRange);
        return this.datePipe.transform(dateRange.From.Date, format) ?? value;
    }

    private getRecordTypeTranslation(filterValue: string): string {
        return this.recordTypeTranslationService.getRecordTypeTranslation(filterValue as RecordType);
    }

    private getTranslatedFieldDefinitionValue(field: ProfileField, filterValue: string): string {
        const flatKey = DottedKeyToFlatKey(field.DottedKey);
        const translateKey = `overrides.${flatKey}.${filterValue}`;
        const translation: string = this.translateService.instant(translateKey);

        return translation === translateKey ? filterValue : translation;
    }

    private getDateFormat(dateRange: DateRange): string {
        // At least 365 to account for leap years
        if (dateRange.LengthInDays >= 365) {
            return YEAR;
        }
        // At least 28 to account for february
        if (dateRange.LengthInDays >= 28) {
            return MONTH_YEAR;
        }
        return DEFAULT_FORMAT;
    }
}
